import { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { MODULE_INTEGRATION_META } from './moduleIntegrationMeta';
import { IntegrationNestedPreviewContext } from './integrationNestedPreviewContext';
import AdditionalSupportPanel from './AdditionalSupportPanel';

const customStyles = {
  content: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '92%',
    maxWidth: '1200px',
    maxHeight: '92vh',
    overflowY: 'auto',
    padding: '16px 20px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    opacity: 1,
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 3100,
  },
};

Modal.setAppElement('#root');

/**
 * Popup con Preview/View del módulo destino (tras select en Redux).
 * nested=true: sin NavbarIcons; en Technology se puede alternar preview↔view in-place.
 */
const IntegrationRecordViewModal = ({
  isOpen,
  onClose,
  moduleKey,
  sourceModuleKey = null,
  originIds = null,
  bridgeKind = null,
}) => {
  const { t } = useTranslation();
  const meta = MODULE_INTEGRATION_META[moduleKey];
  const [detailMode, setDetailMode] = useState('preview');
  const moduleState = useSelector((state) => state[moduleKey] || {});
  const responseKey = meta?.responseKey;
  const selected =
    (responseKey && moduleState[`${responseKey}Selected`]) ||
    moduleState[`${moduleKey}Selected`] ||
    null;
  const referenceId = selected?.id || null;

  useEffect(() => {
    if (isOpen) setDetailMode('preview');
  }, [isOpen, moduleKey]);

  const nestedCtx = useMemo(
    () => ({
      nested: true,
      detailMode,
      setDetailMode,
    }),
    [detailMode]
  );

  const PreviewComponent = meta?.PreviewComponent;
  const ViewComponent = meta?.ViewComponent;

  // Con ViewComponent: toggle in-modal. Resto: solo Preview.
  const BodyComponent =
    detailMode === 'view' && ViewComponent ? ViewComponent : PreviewComponent;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="integration-record-modal"
      style={customStyles}
      contentLabel={t(meta?.labelKey || 'semanticSearch.integration.title')}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>{t(meta?.labelKey || 'semanticSearch.integration.title')}</strong>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="integration-record-view-body">
        <IntegrationNestedPreviewContext.Provider value={nestedCtx}>
          {BodyComponent ? <BodyComponent /> : null}
          <AdditionalSupportPanel
            moduleKey={moduleKey}
            referenceId={referenceId}
            sourceModule={sourceModuleKey}
            originIds={originIds}
            bridgeKind={bridgeKind}
            searchText={
              typeof localStorage !== 'undefined'
                ? localStorage.getItem('searchTextLocal') || ''
                : ''
            }
          />
        </IntegrationNestedPreviewContext.Provider>
      </div>
    </Modal>
  );
};

export default IntegrationRecordViewModal;
